import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

/** A page's code from before a deploy is gone (lazy route chunk): reloading fetches the new one. */
const STALE_CHUNK =
  /dynamically imported module|Importing a module script failed|error loading dynamically/i;
const RELOADED = 'cf-stale-chunk-reload';

/**
 * A page opened before an update reloads itself onto the new version — at most once a minute, so
 * a real failure never loops. False when it did not reload.
 */
function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED) ?? 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem(RELOADED, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** Shown instead of a blank screen when a page throws: the user can reload and keep working. */
export function RouteError() {
  const error = useRouteError();
  const { t } = useTranslation();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const message = error instanceof Error ? error.message : '';
  if (STALE_CHUNK.test(message) && reloadOnce()) return null;
  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : message || String(error);
  if (!notFound) console.error(error);
  return (
    <main className="grid min-h-screen place-items-center bg-stone-50 p-6">
      <div className="max-w-md space-y-3 rounded-2xl bg-surface p-8 text-center shadow">
        <h1 className="text-xl font-semibold text-copper-700">
          {notFound ? t('errors.notFoundTitle') : t('errors.title')}
        </h1>
        <p className="text-stone-600">{notFound ? t('errors.notFoundBody') : t('errors.body')}</p>
        {!notFound && <p className="font-mono text-xs text-stone-400">{detail}</p>}
        {notFound ? (
          <Link
            to="/"
            className="touch-target grid w-full place-items-center rounded-lg bg-copper-600 font-semibold text-on-fill"
          >
            {t('errors.home')}
          </Link>
        ) : (
          <button
            onClick={() => window.location.reload()}
            className="touch-target w-full rounded-lg bg-copper-600 font-semibold text-on-fill"
          >
            {t('errors.reload')}
          </button>
        )}
      </div>
    </main>
  );
}
